/**
 * Approve / Decline on a Book My Test approval Task (2026-10-17).
 *
 * "Card needed" and "Terms requested" requests can be scheduled only with
 * Regina's or Corbin's OK. The Task goes to both; whoever answers first
 * closes it for everyone. Anyone else sees who it is waiting on.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface Approval {
    id: string;
    kind: "card_needed" | "terms_requested";
    status: "pending" | "approved" | "declined" | "not_needed";
    decidedBy: string | null;
    canDecide: boolean;
    approverNames: string;
}

export function BookingApprovalPanel({ taskId, onDecided }: { taskId: string; onDecided?: () => void }) {
    const qc = useQueryClient();
    const q = useQuery({
        queryKey: ["book-my-test", "approval", taskId],
        queryFn: () => api.get<{ approval: Approval | null }>(`/api/book-my-test/approvals/by-task/${taskId}`),
        retry: false,
    });
    const decide = useMutation({
        mutationFn: (v: { id: string; decision: "approve" | "decline" }) =>
            api.post(`/api/book-my-test/approvals/${v.id}/${v.decision}`, {}),
        onSuccess: (_r, v) => {
            toast.success(
                v.decision === "approve" ? "Approved. The office can schedule it now." : "Declined. It stays locked.",
            );
            qc.invalidateQueries({ queryKey: ["book-my-test"] });
            qc.invalidateQueries({ queryKey: ["tasks"] });
            onDecided?.();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "That didn't save. Try again."),
    });
    const a = q.data?.approval;
    if (!a) return null;
    const what = a.kind === "card_needed" ? "scheduling without a card" : "Net 30 terms";
    if (a.status !== "pending") {
        return (
            <div className="rounded-lg border bg-muted/40 p-3 text-sm" data-testid="booking-approval-done">
                {a.status === "approved"
                    ? `${a.decidedBy ?? "Someone"} approved ${what}.`
                    : a.status === "declined"
                      ? `${a.decidedBy ?? "Someone"} declined ${what}.`
                      : "This no longer needs an OK."}
            </div>
        );
    }
    if (!a.canDecide) {
        return (
            <div className="rounded-lg border bg-muted/40 p-3 text-sm" data-testid="booking-approval-waiting">
                Waiting on {a.approverNames} to OK {what}.
            </div>
        );
    }
    return (
        <div className="grid gap-1.5" data-testid="booking-approval-actions">
            <div className="flex gap-2">
                <Button
                    className="flex-1 bg-emerald-700 text-white hover:bg-emerald-800"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ id: a.id, decision: "approve" })}
                >
                    <Check className="mr-1 h-4 w-4" /> Approve
                </Button>
                <Button
                    variant="outline"
                    className="flex-1"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ id: a.id, decision: "decline" })}
                >
                    <X className="mr-1 h-4 w-4" /> Decline
                </Button>
            </div>
            <p className="text-xs text-foreground/70">
                Answer with Approve or Decline. Done by itself doesn&rsquo;t unlock it.
            </p>
        </div>
    );
}
